/**
 * Workflow-runs TABLE contract — the per-workflow run ledger as an offset,
 * sortable, filterable page (pairs with `paginatedSchema` + `<TablePager>` +
 * `usePagedList`, not the keyset feed in `agent-runs.ts`).
 *
 * The keyset feed (`agentRunsFeed`) is the fleet-wide "load more" history — no
 * total, id-DESC only. This one is scoped to ONE loop and answers a different
 * question: "let me sort these by any column, filter them, and jump to page N".
 * That needs a total and a sort whitelist, which is what offset pagination +
 * `paginate()` provide. Read-only; runs are written by the runner.
 *
 * Server mirror: `listWorkflowRunsPage` in `excellent/src/agents/db.ts`. The
 * `sort` enum keys map to SQL columns in the `sortColumns` whitelist there — add
 * a key in BOTH places or a header sorts by nothing.
 */

import { z } from "zod";
import type { Operation } from "../types";
import { paginationQuery, paginatedSchema } from "./_common";

/** The ledger's five statuses (mirrors the excellent_worker_runs CHECK). */
export const workflowRunStatus = z.enum([
  "running",
  "completed",
  "failed",
  "budget_exceeded",
  "aborted",
]);

/** One table row — the worker-run columns this ledger renders. */
export const workflowRunRowSchema = z.object({
  id: z.number(),
  role: z.string(),
  status: workflowRunStatus,
  /** 'pass' on a verified completion; null while running or on a bad ending. */
  outcome: z.string().nullable(),
  /** Raw exit marker, verbatim — surfaced as a tooltip on non-clean rows. */
  exit_reason: z.string().nullable(),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  tasks_verified: z.number(),
  cost_usd: z.number().nullable(),
  wall_ms: z.number().nullable(),
  model: z.string().nullable(),
});
export type WorkflowRunRowDto = z.infer<typeof workflowRunRowSchema>;

/** Sortable columns (whitelist mirrored server-side in `listWorkflowRunsPage`). */
export const workflowRunSort = z.enum([
  "id",
  "role",
  "status",
  "started",
  "verified",
  "cost",
  "duration",
  "model",
]);
export type WorkflowRunSort = z.infer<typeof workflowRunSort>;

export const workflowRunsQuery = paginationQuery.extend({
  /** Required — the runs table is always scoped to one workflow (loop). */
  loop_id: z.coerce.number().int(),
  role: z.string().optional(),
  status: workflowRunStatus.optional(),
  outcome: z.enum(["pass", "fail"]).optional(),
  /** The three non-completing endings at once (failed · budget_exceeded ·
   *  aborted) — the "didn't finish" scope a pipeline step's drill-in opens on.
   *  Composes with `role`; supersedes `status` when both are sent. */
  failed_only: z.coerce.boolean().optional(),
  /** Inclusive day bounds on `started_at` (ISO `YYYY-MM-DD`). */
  from: z.string().optional(),
  to: z.string().optional(),
  sort: workflowRunSort.optional(),
  dir: z.enum(["asc", "desc"]).optional(),
});
export type WorkflowRunsQuery = z.infer<typeof workflowRunsQuery>;

export const workflowRunsList: Operation<
  void,
  z.infer<typeof workflowRunsQuery>,
  void,
  z.infer<ReturnType<typeof paginatedSchema<typeof workflowRunRowSchema>>>
> = {
  method: "GET",
  path: () => "/api/agents/runs/table",
  query: workflowRunsQuery,
  response: paginatedSchema(workflowRunRowSchema),
};

// ── Activity chart: per-day run aggregate ────────────────────────────────────
//
// One row per calendar day the loop ran: total runs, completed (status
// 'completed'), running (status 'running') — the failed line is derived client-
// side as runs − completed − running so the three lines partition the total.
// Loop-scoped + an optional trailing window (`days`); no per-run filters, because
// the chart is the range SELECTOR, not a filtered view.

export const runsByDayRowSchema = z.object({
  day: z.string(),
  runs: z.number(),
  completed: z.number(),
  running: z.number(),
  cost: z.number(),
});
export type RunsByDayRowDto = z.infer<typeof runsByDayRowSchema>;

export const runsByDayQuery = z.object({
  loop_id: z.coerce.number().int(),
  /** Trailing window in days (e.g. 30). Omit for all-time. */
  days: z.coerce.number().int().min(1).max(3650).optional(),
});

export const workflowRunsByDay: Operation<
  void,
  z.infer<typeof runsByDayQuery>,
  void,
  { rows: RunsByDayRowDto[] }
> = {
  method: "GET",
  path: () => "/api/agents/runs/by-day",
  query: runsByDayQuery,
  response: z.object({ rows: z.array(runsByDayRowSchema) }),
};

// ── Failure rollup: the pipeline drill-in's "why they didn't finish" ─────────
//
// Grouped by the raw `exit_reason` (never pre-bucketed — the page buckets it via
// `classifyRunExit`, same doctrine as the run rows). `total` is the ledger total
// for the scope, so the summary can never understate itself from a sampled page.

export const runFailureRowSchema = z.object({
  exit_reason: z.string().nullable(),
  status: z.string(),
  count: z.number(),
  latest_run_id: z.number().nullable(),
});
export type RunFailureRowDto = z.infer<typeof runFailureRowSchema>;

export const runFailuresQuery = z.object({
  loop_id: z.coerce.number().int(),
  role: z.string().optional(),
});

export const workflowRunFailures: Operation<
  void,
  z.infer<typeof runFailuresQuery>,
  void,
  { total: number; rows: RunFailureRowDto[] }
> = {
  method: "GET",
  path: () => "/api/agents/runs/failures",
  query: runFailuresQuery,
  response: z.object({ total: z.number(), rows: z.array(runFailureRowSchema) }),
};

export const workflowRuns = {
  list: workflowRunsList,
  byDay: workflowRunsByDay,
  failures: workflowRunFailures,
};
